import { getConsentMode, subscribeConsentMode } from '../../services/consentMode';

/** Per-document storage, also used when browser persistence is technically unavailable. */
class MemoryStorage implements Storage {
    private readonly values = new Map<string, string>();
    get length(): number {
        return this.values.size;
    }
    clear(): void {
        this.values.clear();
    }
    getItem(key: string): string | null {
        return this.values.get(key) ?? null;
    }
    key(index: number): string | null {
        return [...this.values.keys()][index] ?? null;
    }
    removeItem(key: string): void {
        this.values.delete(key);
    }
    setItem(key: string, value: string): void {
        this.values.set(String(key), String(value));
    }
}

type StorageKind = 'localStorage' | 'sessionStorage';
const memory = { localStorage: new MemoryStorage(), sessionStorage: new MemoryStorage() };
const unavailable = { localStorage: false, sessionStorage: false };

function nativeStorage(kind: StorageKind): Storage | null {
    if (typeof window === 'undefined') return null;
    try {
        return window[kind] ?? null;
    } catch {
        return null;
    }
}

function isRoseKey(key: string): boolean {
    return (
        key === 'rose' ||
        key === 'llm_evaluations' ||
        /^(rose_|inboundx_|rw_|evaluations_|ai_thread_id_|conversation_)/.test(key)
    );
}

/** A retained handle stays consent-aware: callers never receive native Storage. */
function facade(kind: StorageKind): Storage {
    const fallback = memory[kind];
    const read = <T>(operation: (storage: Storage) => T): T => {
        if (getConsentMode() === 'full' && !unavailable[kind]) {
            const storage = nativeStorage(kind);
            if (storage) {
                try {
                    return operation(storage);
                } catch {
                    unavailable[kind] = true;
                }
            }
        }
        return operation(fallback);
    };
    return {
        get length() {
            return read((storage) => storage.length);
        },
        key: (index) => read((storage) => storage.key(index)),
        getItem(key) {
            const value = read((storage) => storage.getItem(key));
            if (value !== null) fallback.setItem(key, value);
            return value;
        },
        setItem(key, value) {
            fallback.setItem(key, value);
            if (getConsentMode() === 'full' && !unavailable[kind]) {
                try {
                    const storage = nativeStorage(kind);
                    if (storage) storage.setItem(key, value);
                    else unavailable[kind] = true;
                } catch {
                    unavailable[kind] = true;
                }
            }
        },
        removeItem(key) {
            fallback.removeItem(key);
            if (getConsentMode() === 'full' && !unavailable[kind]) {
                try {
                    nativeStorage(kind)?.removeItem(key);
                } catch {
                    unavailable[kind] = true;
                }
            }
        },
        clear() {
            // Never clear host-page keys. This facade owns Rose's namespace only.
            const keys = Array.from({ length: this.length }, (_, i) => this.key(i));
            for (const key of keys) if (key && isRoseKey(key)) this.removeItem(key);
        },
    };
}

const local = facade('localStorage');
const session = facade('sessionStorage');
export function getLocalStorage(): Storage {
    return local;
}
export function getSessionStorage(): Storage {
    return session;
}
export function getLocalStorageIfAvailable(): Storage | null {
    return typeof window === 'undefined' ? null : local;
}
export function getSessionStorageIfAvailable(): Storage | null {
    return typeof window === 'undefined' ? null : session;
}
export function isLocalStorageUsable(): boolean {
    return typeof window !== 'undefined';
}

/** Explicit withdrawal cleanup removes existing Rose persistence, never host data. */
export function clearConsentPersistence(): void {
    for (const kind of ['localStorage', 'sessionStorage'] as const) {
        const storage = nativeStorage(kind);
        if (storage) {
            try {
                const keys = Array.from({ length: storage.length }, (_, i) => storage.key(i));
                for (const key of keys) if (key && isRoseKey(key)) storage.removeItem(key);
            } catch {
                /* A blocked storage area cannot be cleared by this page. */
            }
        }
        memory[kind].clear();
    }
}

subscribeConsentMode((mode, previous) => {
    if (mode === 'full' && previous !== 'full') {
        for (const kind of ['localStorage', 'sessionStorage'] as const) {
            const source = memory[kind];
            const target = nativeStorage(kind);
            if (!target || unavailable[kind]) continue;
            for (let i = 0; i < source.length; i++) {
                const key = source.key(i);
                if (key === null) continue;
                const value = source.getItem(key);
                if (value !== null) {
                    try {
                        target.setItem(key, value);
                    } catch {
                        unavailable[kind] = true;
                        break;
                    }
                }
            }
        }
    } else if (previous === 'full') {
        memory.localStorage.clear();
        memory.sessionStorage.clear();
    }
});
